import React from "react";
import { Box, FormControl, FormLabel, Option, Sheet, Stack, Switch } from "@mui/joy";
import { SCreate, SDelete } from "../../../../styles/StyledMUI";
import { colorTokens } from "../../../../styles/globalStyles";
import CommonModal from "../../../common/CommonModal";
import DarkInput from "../../../common/DarkInput";
import DarkSelect from "../../../common/DarkSelect";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import type {
  RentalProduct,
  RentalProductLineRequest,
  RentalProductRequest,
} from "../../../../api/rental/Rental.typed";
import type { EquipmentModel } from "../../../../api/equipment/Equipment.typed";
import {
  TAX_RATES,
  DEFAULT_TAX_RATE,
  formatNum,
  htToTtc,
  ttcToHt,
} from "../../shared/billingUtils";

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (req: RentalProductRequest) => Promise<unknown>;
  initial?: RentalProduct | null;
  models: EquipmentModel[];
};

type LineState = {
  key: number;
  equipment_model_id: number;
  quantity: number;
};

export default function RentalProductFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  models,
}: Props) {
  const lineKey = React.useRef(0);

  const emptyLine = React.useCallback((): LineState => {
    lineKey.current += 1;
    return { key: lineKey.current, equipment_model_id: 0, quantity: 1 };
  }, []);

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [priceHT, setPriceHT] = React.useState(0);
  const [ttcInput, setTtcInput] = React.useState("0");
  const [taxRate, setTaxRate] = React.useState(DEFAULT_TAX_RATE);
  const [currency, setCurrency] = React.useState("EUR");
  const [isActive, setIsActive] = React.useState(true);
  const [lines, setLines] = React.useState<LineState[]>(() => {
    lineKey.current += 1;
    return [{ key: lineKey.current, equipment_model_id: 0, quantity: 1 }];
  });
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open && initial) {
      setName(initial.name);
      setDescription(initial.description ?? "");
      setPriceHT(initial.price_ht);
      setTtcInput(formatNum(htToTtc(initial.price_ht, initial.tax_rate ?? DEFAULT_TAX_RATE)));
      setTaxRate(initial.tax_rate ?? DEFAULT_TAX_RATE);
      setCurrency(initial.currency || "EUR");
      setIsActive(initial.is_active);
      setLines(
        (initial.lines ?? []).length > 0
          ? (initial.lines ?? []).map((l) => {
              lineKey.current += 1;
              return {
                key: lineKey.current,
                equipment_model_id: l.equipment_model_id,
                quantity: l.quantity,
              };
            })
          : [emptyLine()],
      );
    } else if (open) {
      setName("");
      setDescription("");
      setPriceHT(0);
      setTtcInput("0");
      setTaxRate(DEFAULT_TAX_RATE);
      setCurrency("EUR");
      setIsActive(true);
      setLines([emptyLine()]);
    }
  }, [open, initial, emptyLine]);

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (key: number) => setLines((prev) => prev.filter((l) => l.key !== key));
  const updateLine = (key: number, patch: Partial<LineState>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const handleConfirm = async () => {
    setSubmitting(true);
    const reqLines: RentalProductLineRequest[] = lines
      .filter((l) => l.equipment_model_id > 0)
      .map((l) => ({ equipment_model_id: l.equipment_model_id, quantity: l.quantity }));

    await onSubmit({
      name,
      description,
      price_ht: priceHT,
      tax_rate: taxRate,
      currency,
      is_active: isActive,
      lines: reqLines,
    });
    setSubmitting(false);
    onClose();
  };

  const isValid = name.trim().length > 0;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={initial ? "Modifier le kit" : "Nouveau kit de location"}
      size="xl"
      fitContentHeight
      onConfirm={handleConfirm}
      confirmLabel={initial ? "Enregistrer" : "Créer"}
      submitDisabled={!isValid}
      loading={submitting}
    >
      <Stack spacing={2}>
        <FormControl required>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Nom</FormLabel>
          <DarkInput value={name} onChange={(e) => setName(e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel sx={{ color: colorTokens.text.muted }}>Description</FormLabel>
          <DarkInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormControl>

        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: 200 }}>
            <FormLabel sx={{ color: colorTokens.text.muted }}>TVA</FormLabel>
            <DarkSelect
              value={taxRate}
              onChange={(_, v) => {
                if (v == null) return;
                const rate = v as number;
                setTaxRate(rate);
                setTtcInput(formatNum(htToTtc(priceHT, rate)));
              }}
            >
              {TAX_RATES.map((r) => (
                <Option key={r.value} value={r.value}>
                  {r.label}
                </Option>
              ))}
            </DarkSelect>
          </FormControl>
          <FormControl sx={{ width: 120 }}>
            <FormLabel sx={{ color: colorTokens.text.muted }}>Devise</FormLabel>
            <DarkSelect value={currency} onChange={(_, v) => v != null && setCurrency(v as string)}>
              <Option value="EUR">EUR</Option>
              <Option value="USD">USD</Option>
              <Option value="GBP">GBP</Option>
              <Option value="CHF">CHF</Option>
            </DarkSelect>
          </FormControl>
        </Stack>

        <Sheet
          sx={{
            backgroundColor: colorTokens.bg.surfaceMuted,
            border: `1px solid ${colorTokens.border.default}`,
            borderRadius: "8px",
            p: 2,
          }}
        >
          <DarkTypography level="title-sm" sx={{ mb: 1.5 }}>
            Calculette de tarif journalier
          </DarkTypography>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <FormControl required sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Tarif journalier HT</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={priceHT}
                onChange={(e) => {
                  const ht = parseFloat(e.target.value) || 0;
                  setPriceHT(ht);
                  setTtcInput(formatNum(htToTtc(ht, taxRate)));
                }}
              />
            </FormControl>
            <DarkTypography level="body-lg" sx={{ pb: 0.5 }}>
              ⇄
            </DarkTypography>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={{ color: colorTokens.text.muted }}>Tarif journalier TTC</FormLabel>
              <DarkInput
                type="number"
                slotProps={{ input: { step: "0.01", min: "0" } }}
                value={ttcInput}
                onChange={(e) => {
                  setTtcInput(e.target.value);
                  const ttc = parseFloat(e.target.value) || 0;
                  setPriceHT(ttcToHt(ttc, taxRate));
                }}
              />
            </FormControl>
          </Stack>
          <DarkTypography level="body-xs" colorVariant="muted" sx={{ mt: 1 }}>
            Tarif facturé par jour de location. Seul le HT est enregistré ; le TTC est calculé avec
            la TVA sélectionnée.
          </DarkTypography>
        </Sheet>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <DarkTypography level="body-sm">Actif</DarkTypography>
          <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        </Box>

        <Box>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}
          >
            <DarkTypography level="title-sm">Composition</DarkTypography>
            <IconButtonCTA aria-label="Ajouter un modèle au kit" size="sm" onClick={addLine}>
              <SCreate />
            </IconButtonCTA>
          </Box>
          <Sheet
            sx={{
              backgroundColor: colorTokens.bg.surfaceMuted,
              border: `1px solid ${colorTokens.border.default}`,
              borderRadius: "8px",
              p: 1.5,
            }}
          >
            <Stack spacing={1}>
              {lines.map((line) => (
                <Stack key={line.key} direction="row" spacing={1} alignItems="flex-end">
                  <FormControl sx={{ flex: 2 }}>
                    <FormLabel sx={{ color: colorTokens.text.muted, fontSize: "0.75rem" }}>
                      Modèle d'équipement
                    </FormLabel>
                    <DarkSelect
                      value={line.equipment_model_id || undefined}
                      placeholder="Choisir..."
                      onChange={(_, v) => {
                        if (v == null) return;
                        updateLine(line.key, { equipment_model_id: v as number });
                      }}
                    >
                      {models.map((m) => (
                        <Option key={m.ID} value={m.ID}>
                          {m.Name}
                        </Option>
                      ))}
                    </DarkSelect>
                  </FormControl>
                  <FormControl sx={{ width: 80 }}>
                    <FormLabel sx={{ color: colorTokens.text.muted, fontSize: "0.75rem" }}>
                      Qté
                    </FormLabel>
                    <DarkInput
                      type="number"
                      slotProps={{ input: { min: "1" } }}
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(line.key, { quantity: parseInt(e.target.value) || 1 })
                      }
                    />
                  </FormControl>
                  <IconButtonCTA
                    aria-label="Retirer ce modèle"
                    size="sm"
                    ctaVariant="danger"
                    onClick={() => removeLine(line.key)}
                    disabled={lines.length <= 1}
                  >
                    <SDelete />
                  </IconButtonCTA>
                </Stack>
              ))}
            </Stack>
          </Sheet>
        </Box>
      </Stack>
    </CommonModal>
  );
}
